/**
 * What went wrong, in terms the UI can act on.
 *
 * Two rules shape this file.
 *
 * **Nothing raw reaches the screen.** The backend's `detail` is written for
 * whoever is reading the logs - it can contain a SQLAlchemy message, an
 * exception class, a filesystem path. Those are kept on the error object for
 * the console and are never rendered. Every user-facing sentence is chosen by
 * the frontend, in Turkish, from the *kind* of failure.
 *
 * **The kinds are distinguished because the user's next move differs.** A 409
 * means someone else already did the thing; a 404 means it is gone; a network
 * failure means try again. Collapsing them into "bir hata oluştu" throws away
 * the only information that would have helped.
 */

export type ApiErrorKind =
  /** The request never reached the server: offline, DNS, refused, CORS. */
  | "network"
  /** The request was abandoned - navigation, or an explicit cancel. */
  | "canceled"
  /** Took too long. Distinct from `network`: the server may still be working. */
  | "timeout"
  /** 400/422 - the request itself was not acceptable. */
  | "validation"
  /** 404 - the thing is not there. */
  | "not_found"
  /** 409 - it already exists, or the state moved under us. */
  | "conflict"
  /** 413 - the upload is larger than the server accepts. */
  | "too_large"
  /** 415 - the file type is not on the server's allow-list. */
  | "unsupported_media"
  /** 503 - a dependency the endpoint needs is not available right now. */
  | "unavailable"
  /** 5xx - the server broke. Nothing the user can do differently. */
  | "server"
  /** A response arrived but was not what the contract promised. */
  | "malformed"
  /** Anything not covered above. */
  | "unknown";

/**
 * Machine-readable refusals, mirroring `sentyform.api.errors`.
 *
 * Unlike `detail`, a code is safe to branch on: it is a closed vocabulary
 * chosen by the server, never free text and never derived from input. It
 * exists for the cases where the status alone is ambiguous - a reconstruction
 * refused for too few frames and one refused for too many are both 409, and
 * the user has to do the opposite thing in each case.
 */
export type ApiErrorCode = "not_enough_input" | "unknown_profile";

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  /**
   * The server's own words. **Diagnostic only.** Logged, inspected in tests,
   * never rendered - it may carry a path, a table name or a traceback.
   */
  readonly detail: string | null;
  /** The server's machine-readable refusal, when it sent one. Safe to branch on. */
  readonly code: string | null;
  readonly url: string;

  constructor(args: {
    kind: ApiErrorKind;
    status?: number | null;
    detail?: string | null;
    code?: string | null;
    url: string;
    cause?: unknown;
  }) {
    // The Error message is for developers; it is never the string shown to a
    // user. `messageFor` below produces that.
    super(`${args.kind}${args.status ? ` (${args.status})` : ""}: ${args.url}`, {
      cause: args.cause,
    });
    this.name = "ApiError";
    this.kind = args.kind;
    this.status = args.status ?? null;
    this.detail = args.detail ?? null;
    this.code = args.code ?? null;
    this.url = args.url;
  }

  /** True when retrying the identical request could plausibly succeed. */
  get retryable(): boolean {
    return (
      this.kind === "network" || this.kind === "timeout" || this.kind === "server" ||
      this.kind === "unavailable"
    );
  }
}

export function kindForStatus(status: number): ApiErrorKind {
  switch (status) {
    case 400:
    case 422:
      return "validation";
    case 404:
      return "not_found";
    case 409:
      return "conflict";
    case 413:
      return "too_large";
    case 415:
      return "unsupported_media";
    case 503:
      return "unavailable";
    default:
      return status >= 500 ? "server" : "unknown";
  }
}

/**
 * FastAPI's `detail` is a string for our own errors and an array of validation
 * objects for a 422 it raised itself. Both are reduced to one diagnostic
 * string; neither is shown to anyone.
 */
export function extractDetail(body: unknown): string | null {
  if (typeof body === "string") return body || null;
  if (!body || typeof body !== "object") return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail || null;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((entry) => {
        if (!entry || typeof entry !== "object") return null;
        const { loc, msg } = entry as { loc?: unknown; msg?: unknown };
        const where = Array.isArray(loc) ? loc.join(".") : "";
        return typeof msg === "string" ? (where ? `${where}: ${msg}` : msg) : null;
      })
      .filter((part): part is string => part !== null);
    return parts.length > 0 ? parts.join("; ") : null;
  }
  return null;
}

/**
 * The refusal code, when the body carries one.
 *
 * Anything that is not a plain non-empty string is dropped: a client that
 * branches on this must never be steered by a shape the contract does not
 * promise.
 */
export function extractCode(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const code = (body as { code?: unknown }).code;
  return typeof code === "string" && code.length > 0 ? code : null;
}
